import React, { useMemo } from "react";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants.ts";
import SingleFile from "@components/FileUpload/SingleFile.tsx";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { type PortServicesOutletContext } from "../PortServicesContainer";
import type { PortServicesFormData } from "../schema/portServicesFormSchema";
import { usePortServiceDocumentUpload } from "./utils";

const LANG_BASE = "vessel-call.port-services";
const DEFAULT_FIELD_NAME = "attachmentDocuments" as const;
const DEFAULT_TEST_ID = "portService-attachment-documents";

type PortServiceUploadFilesProps = {
  title: string;
  fileType?: string;
  fieldName?: keyof PortServicesFormData;
  maxFiles?: number;
  testId?: string;
  children?: React.ReactNode;
};

const PortServiceUploadFiles: React.FC<PortServiceUploadFilesProps> = ({
  title,
  fileType,
  fieldName = DEFAULT_FIELD_NAME,
  maxFiles = 1,
  testId = DEFAULT_TEST_ID,
  children,
}) => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useOutletContext<PortServicesOutletContext>();
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const { portServicesRequestFile: uploadConditions } = FILE_UPLOAD_PROFILES;

  const { files, setFiles, handleFieldChangeForAzure, removeFileAtIndex, isUploading } =
    usePortServiceDocumentUpload({
      fieldName,
      certificateType: fileType || "PORT_SERVICES",
      formData,
      updateFormData,
    });
  const allowedExtensions = useMemo(
    () =>
      (uploadConditions.format ?? []).map((ext) =>
        ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
      ),
    [uploadConditions.format],
  );

  return (
    <FormProvider {...formData}>
      <FormSection isFullWidth title={t(`${LANG_BASE}.fields.${title}`)}>
        <div className="rounded-sm relative">
          {isUploading && (
            <div className="absolute inset-0 flex justify-center pointer-events-none top-30 opacity-100">
              <RotateRightIcon
                fontSize="large"
                sx={{
                  "animation": "mui-spin 0.9s linear infinite",
                  "@keyframes mui-spin": { to: { transform: "rotate(360deg)" } },
                }}
              />
            </div>
          )}
          <div className={`${isUploading ? "opacity-50" : ""} p-4`}>
            <FormItem<PortServicesFormData, keyof PortServicesFormData>
              name={fieldName}
              type="file"
              testId={testId}
              uploadConditions={uploadConditions}
              maxFiles={maxFiles}
              allowedFileExtensions={allowedExtensions}
              uploadDescription={t("upload.additional-documents-upload", {
                extensions: uploadConditions.format.join(", "),
                size: uploadConditions.maxSizeLabel,
              })}
              disabled={isUploading}
              onUpload={handleFieldChangeForAzure}
              files={files}
              setFiles={setFiles}
            />
          </div>
        </div>

        {files.length
          ? files.map((file, idx) => (
              <div className="flex-1" key={`${file.name}-${(file as File).lastModified ?? idx}`}>
                <SingleFile
                  testId={`${String(fieldName)}-${idx}`}
                  file={file}
                  currentFileIndex={idx}
                  fieldName={String(fieldName)}
                  setFiles={() => removeFileAtIndex(idx)}
                />
              </div>
            ))
          : null}

        {children}
      </FormSection>
    </FormProvider>
  );
};

export default PortServiceUploadFiles;
